import { type ReactNode, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ORDER_PAYMENT_STATES, type OrderListItem, type OrderPaymentState } from '@aps/contracts';
import {
  Button,
  DataTable,
  EmptyState,
  Skeleton,
  formatDate,
  formatMoney,
  type DataTableColumn,
} from '@aps/ui';
import { ApiError, api } from '../lib/api.js';
import { messages } from '../i18n/messages.js';
import { useSession } from '../app/session-context.js';
import { QueueError } from './WorkQueueScreen.js';

interface OrdersScreenProps {
  onOpenOrder: (orderId: string) => void;
}

const PAGE_SIZE = 25;

/**
 * To'lov holati yorliqlari.
 *
 * Ro'yxatning o'zi KONTRAKTDAN (`ORDER_PAYMENT_STATES`) — bu yerda qayta
 * yozilmaydi. `Record` tipi tufayli yangi holat qo'shilsa va tarjimasi
 * yozilmasa `tsc` yiqiladi.
 */
export const PAYMENT_LABELS: Record<OrderPaymentState, { UZ: string; RU: string }> = {
  UNPAID: { UZ: "To'lanmagan", RU: 'Не оплачен' },
  PARTIAL: { UZ: 'Qisman', RU: 'Частично' },
  PAID: { UZ: "To'langan", RU: 'Оплачен' },
  OVERPAID: { UZ: 'Ortiqcha', RU: 'Переплата' },
};

/**
 * Buyurtmalar ro'yxati.
 *
 * Bu ekran ASOSIY EMAS — asosiysi ish navbati. Bu ro'yxat qidiruv va
 * mijoz qo'ng'irog'i uchun (docs/02-MAHSULOT.md → "Admin ish navbati").
 */
export function OrdersScreen({ onOpenOrder }: OrdersScreenProps): ReactNode {
  const { locale } = useSession();
  const t = messages(locale);

  const [search, setSearch] = useState('');
  const [payment, setPayment] = useState<OrderPaymentState | null>(null);
  const [cursors, setCursors] = useState<string[]>([]);

  const cursor = cursors.at(-1);
  const trimmed = search.trim();

  const page = useQuery({
    queryKey: ['orders', trimmed, payment, cursor ?? null],
    queryFn: () =>
      api.orders({
        limit: PAGE_SIZE,
        ...(trimmed === '' ? {} : { query: trimmed }),
        ...(payment === null ? {} : { paymentState: payment }),
        ...(cursor === undefined ? {} : { cursor }),
      }),
    // Filtr almashganda jadval bo'sh bo'lib ketmasin — eski natija
    // yangisi kelguncha turadi.
    placeholderData: keepPreviousData,
  });

  /** Filtr o'zgarganda sahifalash boshidan boshlanadi. */
  const resetPaging = (): void => {
    setCursors([]);
  };

  const header = (
    <header className="adm-page__header">
      <h1 className="adm-page__title">{t.orders}</h1>
    </header>
  );

  const filters = (
    <div className="adm-filters">
      <input
        className="aps-field__input adm-filters__search"
        type="search"
        placeholder={t.ordersSearch}
        value={search}
        onChange={(event) => {
          setSearch(event.target.value);
          resetPaging();
        }}
      />
      <div className="adm-filters__chips">
        <Button
          variant={payment === null ? 'primary' : 'ghost'}
          size="sm"
          onClick={() => {
            setPayment(null);
            resetPaging();
          }}
        >
          {t.ordersFilterAll}
        </Button>
        {ORDER_PAYMENT_STATES.map((state) => (
          <Button
            key={state}
            variant={payment === state ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => {
              setPayment(state);
              resetPaging();
            }}
          >
            {PAYMENT_LABELS[state][locale]}
          </Button>
        ))}
      </div>
    </div>
  );

  if (page.isPending) {
    return (
      <section className="adm-page">
        {header}
        {filters}
        <Skeleton height="220px" label={t.loading} />
      </section>
    );
  }

  if (page.isError) {
    return (
      <section className="adm-page">
        {header}
        {filters}
        <QueueError
          message={page.error instanceof ApiError ? page.error.text(locale) : t.errorTitle}
          retryLabel={t.retry}
          onRetry={() => void page.refetch()}
        />
      </section>
    );
  }

  const columns: DataTableColumn<OrderListItem>[] = [
    {
      key: 'number',
      header: t.colOrder,
      render: (row) => (
        <button
          className="adm-link"
          type="button"
          onClick={() => {
            onOpenOrder(row.id);
          }}
        >
          {row.number}
        </button>
      ),
    },
    {
      key: 'created',
      header: t.colWaiting,
      render: (row) => formatDate(row.createdAt, locale),
    },
    {
      key: 'items',
      header: t.orderItems,
      numeric: true,
      render: (row) => t.itemsCount(row.itemCount),
    },
    {
      key: 'delivery',
      header: t.orderDelivery,
      render: (row) =>
        row.deliveryMethod === 'COURIER'
          ? `${t.deliveryCourierShort}${row.regionNameUz === null ? '' : ` · ${row.regionNameUz}`}`
          : t.deliveryPickupShort,
    },
    {
      key: 'payment',
      header: t.ordersFilterPayment,
      render: (row) => PAYMENT_LABELS[row.paymentState][locale],
    },
    {
      key: 'total',
      header: t.colTotal,
      numeric: true,
      render: (row) => formatMoney(row.grandTotal, locale),
    },
  ];

  return (
    <section className="adm-page">
      {header}
      {filters}

      <DataTable
        caption={t.orders}
        columns={columns}
        rows={page.data.items}
        rowKey={(row) => row.id}
        empty={<EmptyState title={t.ordersEmpty} />}
      />

      {page.data.nextCursor !== null && (
        <div className="adm-table-footer">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              const next = page.data.nextCursor;
              if (next !== null) setCursors((current) => [...current, next]);
            }}
          >
            {t.loadMore}
          </Button>
        </div>
      )}
    </section>
  );
}
